import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * SmartDownloadButton
 * A unified, multi-talented download button with a pop-up modal
 * tailored strictly to institute.feedbackMode.
 * Uses --color-2 for button branding.
 * Supports:
 * - Individual student report download
 * - Bulk download with format choice (Single Merged PDF vs. Separate PDFs ZIP)
 * - Blank paper reordering delegation via onOpenBulkReorderModal
 */
const SmartDownloadButton = ({
  answerPaperId,
  testId,
  feedbackMode = "APP_FEEDBACK_ONLY",
  hasSummary = false,
  studentName = "",
  referenceId = "",
  testName = "Assessment",
  disabled = false,
  serverUrl = "",
  fetchWithAuth,
  swalError,
  swalSuccess,
  isBulk = false,
  buttonText = "",
  iconOnly = true,
  style = {},
  className = "",
  title = "Download options",
  onOpenBulkReorderModal,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [downloadingFormat, setDownloadingFormat] = useState(null); // "merged" | "zip"
  const [bulkFormatTargetOption, setBulkFormatTargetOption] = useState(null);

  // Close modal on Escape key if not actively downloading
  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isDownloading) {
        setIsModalOpen(false);
        setBulkFormatTargetOption(null);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen, isDownloading]);

  // Lock background scroll when modal is open
  useEffect(() => {
    if (!isModalOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isModalOpen]);

  // Determine available options based on feedbackMode
  const getOptions = () => {
    const isAnnotOnly = feedbackMode === "ANNOTATION_ONLY";
    const isAnnotAndApp = feedbackMode === "ANNOTATION_AND_APP_FEEDBACK";

    if (isAnnotOnly) {
      return [
        {
          key: "annot_combined",
          title: "Annotated Paper (with Score Report)",
          desc: "Evaluation marks on submitted paper with complete score report",
          icon: "fa-solid fa-file-pen",
          type: "combined",
          blankPaper: false,
          isRecommended: true,
        },
        {
          key: "annot_blank",
          title: "Annotated Paper (Blank Paper)",
          desc: isBulk
            ? "Teacher marks on blank paper (opens paper stack reorder tool)"
            : "Evaluation marks rendered on clean blank paper",
          icon: "fa-regular fa-file-lines",
          type: "annotation",
          blankPaper: true,
        },
      ];
    }

    if (isAnnotAndApp) {
      return [
        {
          key: "annot_combined",
          title: "Annotated Paper (with Score Report)",
          desc: "Evaluation marks on submitted paper with complete score report",
          icon: "fa-solid fa-file-pen",
          type: "combined",
          blankPaper: false,
          isRecommended: true,
        },
        {
          key: "annot_blank",
          title: "Annotated Paper (Blank Paper)",
          desc: isBulk
            ? "Teacher marks on blank paper (opens paper stack reorder tool)"
            : "Evaluation marks rendered on clean blank paper",
          icon: "fa-regular fa-file-lines",
          type: "annotation",
          blankPaper: true,
        },
        {
          key: "score_only",
          title: "Score Report Only",
          desc: "Detailed question scores & evaluation feedback (without paper)",
          icon: "fa-solid fa-clipboard-check",
          type: "score",
          blankPaper: false,
          includeAnswerPaper: false,
        },
      ];
    }

    // Default: APP_FEEDBACK_ONLY mode
    return [
      {
        key: "score_report",
        title: "Score Report",
        desc: "Detailed score breakdown & performance summary",
        icon: "fa-solid fa-file-pdf",
        type: "score",
        includeAnswerPaper: false,
        isRecommended: true,
      },
      {
        key: "score_with_paper",
        title: "Score Report with Answer Paper",
        desc: "Score report + Performance summary + Student answer sheet",
        icon: "fa-solid fa-paperclip",
        type: "score",
        includeAnswerPaper: true,
      },
    ];
  };

  const executeDownload = async (option, format = "merged") => {
    if (option.disabled || isDownloading) return;
    setIsDownloading(true);
    setDownloadingKey(option.key);
    setDownloadingFormat(format);

    try {
      let url = "";
      let fetchOptions = {
        credentials: "include",
      };

      if (isBulk) {
        url = `${serverUrl}/api/score/generate-bulk-report/${testId}`;
        fetchOptions.method = "POST";
        fetchOptions.headers = { "Content-Type": "application/json" };
        fetchOptions.body = JSON.stringify({
          type: option.type,
          blankPaper: option.blankPaper === true,
          includeAnswerPaper: option.includeAnswerPaper === true,
          format: format, // "merged" or "zip"
        });
      } else {
        const queryParams = new URLSearchParams({
          type: option.type || "combined",
          blankPaper: option.blankPaper ? "true" : "false",
          includeAnswerPaper: option.includeAnswerPaper ? "true" : "false",
        });
        url = `${serverUrl}/api/score/generate-report/${answerPaperId}?${queryParams.toString()}`;
        fetchOptions.method = "GET";
      }

      const response = await fetchWithAuth(url, fetchOptions);

      if (!response || !response.ok) {
        const errJson = await response?.json().catch(() => ({}));
        throw new Error(errJson?.message || "Failed to generate report.");
      }

      const blob = await response.blob();

      // Extract filename from Content-Disposition header if available
      let filename = "";
      const disposition = response.headers?.get("content-disposition");
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      if (!filename) {
        const safeStudent = (studentName || "Student").replace(/[/\\?%*:|"<>]/g, "_").trim();
        const safeRef = String(referenceId || answerPaperId || "Report").replace(/[/\\?%*:|"<>]/g, "_").trim();
        const safeTest = String(testName || "Assessment").replace(/[/\\?%*:|"<>]/g, "_").trim();
        if (isBulk) {
          const ext = format === "zip" ? "zip" : "pdf";
          filename = `Bulk_Score_Reports_${safeTest}.${ext}`;
        } else {
          filename = `${option.title.replace(/\s+/g, "_")}-${safeStudent}(${safeRef}).pdf`;
        }
      }

      // Trigger native browser download
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 5000);

      setIsModalOpen(false);
      setBulkFormatTargetOption(null);

      if (swalSuccess) {
        swalSuccess("Download Started", `${filename} is being downloaded.`);
      }
    } catch (err) {
      console.error("SmartDownloadButton error:", err);
      if (swalError) {
        swalError(err.message || "An error occurred while generating the report.");
      }
    } finally {
      setIsDownloading(false);
      setDownloadingKey(null);
      setDownloadingFormat(null);
    }
  };

  const handleSelectOption = (option) => {
    if (option.disabled || isDownloading) return;

    if (isBulk) {
      // If clicking Blank Paper annotation in bulk, delegate to Reorder Modal
      if (option.key === "annot_blank") {
        setIsModalOpen(false);
        setBulkFormatTargetOption(null);
        if (typeof onOpenBulkReorderModal === "function") {
          onOpenBulkReorderModal();
          return;
        }
      }
      // For any other bulk option, present format choice (Single Merged PDF vs Separate PDFs ZIP)
      setBulkFormatTargetOption(option);
      return;
    }

    // Single student per-row download
    executeDownload(option, "merged");
  };

  const options = getOptions();

  return (
    <>
      <button
        type="button"
        className={`${iconOnly ? "delete-btn" : ""} ${className}`.trim()}
        disabled={disabled || isDownloading}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled && !isDownloading) {
            setBulkFormatTargetOption(null);
            setIsModalOpen(true);
          }
        }}
        title={isDownloading ? "Generating report..." : title}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          minWidth: iconOnly ? "36px" : undefined,
          height: iconOnly ? "36px" : undefined,
          borderRadius: iconOnly ? "50%" : "8px",
          backgroundColor: "var(--color-2, #da1c5c)",
          color: "#ffffff",
          border: "1px solid var(--color-2, #da1c5c)",
          cursor: disabled || isDownloading ? "not-allowed" : "pointer",
          position: "relative",
          transition: "all 0.2s ease-in-out",
          ...style,
        }}
      >
        {isDownloading ? (
          <>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: iconOnly ? "14px" : "13px" }} />
            {buttonText && <span>Generating...</span>}
          </>
        ) : (
          <>
            <i className="fa-solid fa-download" style={{ fontSize: iconOnly ? "14px" : "13px" }} />
            {buttonText && <span>{buttonText}</span>}
          </>
        )}
      </button>

      {/* Pop-up Modal Portal */}
      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                backdropFilter: "blur(3px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 99999,
                padding: "1rem",
                boxSizing: "border-box",
              }}
              onClick={() => {
                if (!isDownloading) {
                  setIsModalOpen(false);
                  setBulkFormatTargetOption(null);
                }
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 15 }}
                transition={{ type: "spring", stiffness: 350, damping: 28 }}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  maxWidth: "520px",
                  width: "100%",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                  position: "relative",
                  overflow: "hidden",
                  textAlign: "left",
                  boxSizing: "border-box",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div
                  style={{
                    padding: "1.25rem 1.5rem",
                    borderBottom: "1px solid #f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1rem",
                    backgroundColor: "#f8fafc",
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                      <div
                        style={{
                          width: "34px",
                          height: "34px",
                          borderRadius: "8px",
                          backgroundColor: "var(--color-2, #da1c5c)",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          flexShrink: 0,
                        }}
                      >
                        <i className={bulkFormatTargetOption ? "fa-solid fa-layer-group" : "fa-solid fa-file-arrow-down"} />
                      </div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1.15rem",
                          fontWeight: 700,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {bulkFormatTargetOption
                          ? "Select Download Format"
                          : isBulk
                          ? "Download All Reports"
                          : "Download Score Report"}
                      </h3>
                    </div>
                    <p
                      style={{
                        margin: 0,
                        paddingLeft: "44px",
                        fontSize: "0.82rem",
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {bulkFormatTargetOption
                        ? `For: ${bulkFormatTargetOption.title}`
                        : isBulk
                        ? `Consolidated reports for all graded students in "${testName}"`
                        : `${studentName || "Student"} ${referenceId ? `(${referenceId})` : ""}`}
                    </p>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <button
                      type="button"
                      disabled={isDownloading}
                      onClick={() => {
                        setIsModalOpen(false);
                        setBulkFormatTargetOption(null);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "15px",
                        color: "#94a3b8",
                        cursor: isDownloading ? "not-allowed" : "pointer",
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isDownloading) {
                          e.currentTarget.style.color = "#0f172a";
                          e.currentTarget.style.backgroundColor = "#e2e8f0";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#94a3b8";
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                      aria-label="Close modal"
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div
                  style={{
                    padding: "1.25rem 1.5rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                    maxHeight: "65vh",
                    overflowY: "auto",
                    overflowX: "hidden", // Prevents horizontal scroll clipping completely
                    boxSizing: "border-box",
                    width: "100%",
                  }}
                >
                  {bulkFormatTargetOption ? (
                    /* Step 2 for Bulk: Choose Single Merged PDF vs Separate PDFs (ZIP) */
                    <div style={{ width: "100%", boxSizing: "border-box" }}>
                      <div style={{ marginBottom: "12px" }}>
                        <button
                          type="button"
                          disabled={isDownloading}
                          onClick={() => setBulkFormatTargetOption(null)}
                          style={{
                            background: "none",
                            border: "none",
                            color: isDownloading ? "#94a3b8" : "var(--color-2, #da1c5c)",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            cursor: isDownloading ? "not-allowed" : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 8px",
                            marginLeft: "-8px",
                            borderRadius: "6px",
                            transition: "background-color 0.15s ease",
                          }}
                          onMouseEnter={(e) => {
                            if (!isDownloading) {
                              e.currentTarget.style.backgroundColor = "rgba(218, 28, 92, 0.08)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          <i className="fa-solid fa-arrow-left" style={{ fontSize: "12px" }} /> Back to report options
                        </button>
                      </div>

                      <p
                        style={{
                          margin: "0 0 0.75rem 0",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Choose Output Format:
                      </p>

                      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
                        {/* Option 1: Single Merged PDF */}
                        {(() => {
                          const isThisDownloading = isDownloading && downloadingFormat === "merged";
                          const isThisDisabled = isDownloading && !isThisDownloading;

                          return (
                            <div
                              role="button"
                              tabIndex={isDownloading ? -1 : 0}
                              onClick={() => !isDownloading && executeDownload(bulkFormatTargetOption, "merged")}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "14px",
                                padding: "14px 16px",
                                borderRadius: "12px",
                                border: isThisDownloading
                                  ? "1.5px solid var(--color-2, #da1c5c)"
                                  : "1.5px solid #e2e8f0",
                                backgroundColor: isThisDownloading
                                  ? "rgba(218, 28, 92, 0.04)"
                                  : "#ffffff",
                                cursor: isDownloading ? "not-allowed" : "pointer",
                                opacity: isThisDisabled ? 0.5 : 1,
                                transition: "all 0.15s ease",
                                width: "100%",
                                boxSizing: "border-box",
                              }}
                              onMouseEnter={(e) => {
                                if (!isDownloading) {
                                  e.currentTarget.style.borderColor = "var(--color-2, #da1c5c)";
                                  e.currentTarget.style.backgroundColor = "rgba(218, 28, 92, 0.04)";
                                  e.currentTarget.style.boxShadow = "0 4px 14px rgba(218, 28, 92, 0.12)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isDownloading) {
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.backgroundColor = "#ffffff";
                                  e.currentTarget.style.boxShadow = "none";
                                }
                              }}
                            >
                              <div
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  borderRadius: "10px",
                                  backgroundColor: "rgba(218, 28, 92, 0.08)",
                                  color: "var(--color-2, #da1c5c)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "19px",
                                  flexShrink: 0,
                                }}
                              >
                                <i className="fa-regular fa-file-pdf" />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "0.96rem", fontWeight: 700, color: "#0f172a", marginBottom: "2px" }}>
                                  Single Merged PDF
                                </div>
                                <div style={{ fontSize: "0.82rem", color: "#64748b", lineHeight: 1.35 }}>
                                  All student reports combined into a single continuous PDF document.
                                </div>
                              </div>
                              <div
                                style={{
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "24px",
                                  height: "24px",
                                }}
                              >
                                {isThisDownloading ? (
                                  <i className="fa-solid fa-spinner fa-spin" style={{ color: "var(--color-2, #da1c5c)", fontSize: "16px" }} />
                                ) : (
                                  <i className="fa-solid fa-chevron-right" style={{ color: "#94a3b8", fontSize: "13px" }} />
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Option 2: Separate PDFs (.ZIP) */}
                        {(() => {
                          const isThisDownloading = isDownloading && downloadingFormat === "zip";
                          const isThisDisabled = isDownloading && !isThisDownloading;

                          return (
                            <div
                              role="button"
                              tabIndex={isDownloading ? -1 : 0}
                              onClick={() => !isDownloading && executeDownload(bulkFormatTargetOption, "zip")}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "14px",
                                padding: "14px 16px",
                                borderRadius: "12px",
                                border: isThisDownloading
                                  ? "1.5px solid var(--color-2, #da1c5c)"
                                  : "1.5px solid #e2e8f0",
                                backgroundColor: isThisDownloading
                                  ? "rgba(218, 28, 92, 0.04)"
                                  : "#ffffff",
                                cursor: isDownloading ? "not-allowed" : "pointer",
                                opacity: isThisDisabled ? 0.5 : 1,
                                transition: "all 0.15s ease",
                                width: "100%",
                                boxSizing: "border-box",
                              }}
                              onMouseEnter={(e) => {
                                if (!isDownloading) {
                                  e.currentTarget.style.borderColor = "var(--color-2, #da1c5c)";
                                  e.currentTarget.style.backgroundColor = "rgba(218, 28, 92, 0.04)";
                                  e.currentTarget.style.boxShadow = "0 4px 14px rgba(218, 28, 92, 0.12)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isDownloading) {
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.backgroundColor = "#ffffff";
                                  e.currentTarget.style.boxShadow = "none";
                                }
                              }}
                            >
                              <div
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  borderRadius: "10px",
                                  backgroundColor: "rgba(218, 28, 92, 0.08)",
                                  color: "var(--color-2, #da1c5c)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "19px",
                                  flexShrink: 0,
                                }}
                              >
                                <i className="fa-solid fa-file-zipper" />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "0.96rem", fontWeight: 700, color: "#0f172a", marginBottom: "2px" }}>
                                  Separate PDFs (.ZIP)
                                </div>
                                <div style={{ fontSize: "0.82rem", color: "#64748b", lineHeight: 1.35 }}>
                                  Individual PDF files for each student packaged into a convenient ZIP archive.
                                </div>
                              </div>
                              <div
                                style={{
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "24px",
                                  height: "24px",
                                }}
                              >
                                {isThisDownloading ? (
                                  <i className="fa-solid fa-spinner fa-spin" style={{ color: "var(--color-2, #da1c5c)", fontSize: "16px" }} />
                                ) : (
                                  <i className="fa-solid fa-chevron-right" style={{ color: "#94a3b8", fontSize: "13px" }} />
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Download Status Notification */}
                      {isDownloading && (
                        <div
                          style={{
                            marginTop: "14px",
                            padding: "10px 14px",
                            borderRadius: "8px",
                            backgroundColor: "rgba(218, 28, 92, 0.06)",
                            border: "1px solid rgba(218, 28, 92, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            color: "var(--color-2, #da1c5c)",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                          }}
                        >
                          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "15px", flexShrink: 0 }} />
                          <span>
                            Generating and packaging {downloadingFormat === "zip" ? "ZIP archive" : "merged PDF"} reports... Please wait.
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Step 1: Report Options List */
                    <>
                      <p
                        style={{
                          margin: "0 0 0.25rem 0",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Select Report Type:
                      </p>

                      {options.map((option) => {
                        const isOptionDownloading = isDownloading && downloadingKey === option.key;
                        const isOptionDisabled = option.disabled || (isDownloading && !isOptionDownloading);

                        return (
                          <div
                            key={option.key}
                            role="button"
                            tabIndex={isOptionDisabled ? -1 : 0}
                            onClick={() => handleSelectOption(option)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "12px 14px",
                              borderRadius: "10px",
                              border: option.isRecommended
                                ? "1.5px solid var(--color-2, #da1c5c)"
                                : "1px solid #e2e8f0",
                              backgroundColor: option.disabled
                                ? "#f8fafc"
                                : option.isRecommended
                                ? "rgba(218, 28, 92, 0.04)"
                                : "#ffffff",
                              cursor: isOptionDisabled ? "not-allowed" : "pointer",
                              opacity: option.disabled ? 0.6 : isDownloading && !isOptionDownloading ? 0.5 : 1,
                              transition: "all 0.15s ease",
                              position: "relative",
                              width: "100%",
                              boxSizing: "border-box",
                            }}
                            onMouseEnter={(e) => {
                              if (!isOptionDisabled) {
                                e.currentTarget.style.borderColor = "var(--color-2, #da1c5c)";
                                e.currentTarget.style.boxShadow = "0 4px 14px rgba(218, 28, 92, 0.12)";
                                if (!option.isRecommended) {
                                  e.currentTarget.style.backgroundColor = "rgba(218, 28, 92, 0.03)";
                                }
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isOptionDisabled) {
                                e.currentTarget.style.borderColor = option.isRecommended
                                  ? "var(--color-2, #da1c5c)"
                                  : "#e2e8f0";
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.backgroundColor = option.isRecommended
                                  ? "rgba(218, 28, 92, 0.04)"
                                  : "#ffffff";
                              }
                            }}
                          >
                            {/* Left Icon Container */}
                            <div
                              style={{
                                width: "38px",
                                height: "38px",
                                borderRadius: "8px",
                                backgroundColor: option.disabled
                                  ? "#e2e8f0"
                                  : option.isRecommended
                                  ? "var(--color-2, #da1c5c)"
                                  : "rgba(218, 28, 92, 0.08)",
                                color: option.disabled
                                  ? "#94a3b8"
                                  : option.isRecommended
                                  ? "#ffffff"
                                  : "var(--color-2, #da1c5c)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "16px",
                                flexShrink: 0,
                              }}
                            >
                              <i className={option.icon} />
                            </div>

                            {/* Text info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                <span
                                  style={{
                                    fontSize: "0.94rem",
                                    fontWeight: 600,
                                    color: option.disabled ? "#94a3b8" : "#1e293b",
                                  }}
                                >
                                  {option.title}
                                </span>
                                {option.isRecommended && (
                                  <span
                                    style={{
                                      fontSize: "0.68rem",
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      backgroundColor: "var(--color-2, #da1c5c)",
                                      color: "#ffffff",
                                      letterSpacing: "0.4px",
                                    }}
                                  >
                                    Recommended
                                  </span>
                                )}
                              </div>
                              <span
                                style={{
                                  display: "block",
                                  fontSize: "0.8rem",
                                  color: "#64748b",
                                  marginTop: "2px",
                                  lineHeight: 1.35,
                                }}
                              >
                                {option.disabled && option.disabledReason
                                  ? `(${option.disabledReason})`
                                  : option.desc}
                              </span>
                            </div>

                            {/* Right Arrow / Loader */}
                            <div
                              style={{
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "24px",
                                height: "24px",
                              }}
                            >
                              {isOptionDownloading ? (
                                <i className="fa-solid fa-spinner fa-spin" style={{ color: "var(--color-2, #da1c5c)", fontSize: "16px" }} />
                              ) : (
                                <i className="fa-solid fa-chevron-right" style={{ color: "#94a3b8", fontSize: "13px" }} />
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Download Status Notification for Step 1 */}
                      {isDownloading && (
                        <div
                          style={{
                            marginTop: "6px",
                            padding: "10px 14px",
                            borderRadius: "8px",
                            backgroundColor: "rgba(218, 28, 92, 0.06)",
                            border: "1px solid rgba(218, 28, 92, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            color: "var(--color-2, #da1c5c)",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                          }}
                        >
                          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "15px", flexShrink: 0 }} />
                          <span>Generating score report PDF... Please wait.</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default SmartDownloadButton;
